import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { hasProperty, isNotNull } from '@fmnts/core';

import { ControlPath } from '../angular-forms.types';

type ControlResolver = (
  control: AbstractControl,
  paths: ControlPath[],
) => AbstractControl[];

export type RequiresAnyValidationError = ControlPath[];

const VALIDATOR_ERROR_PROP_NAME = 'any';

/**
 * Type guard that checks if any `ValidationErrors` contain
 * the errors from the {@link requiresAny}.
 *
 * @param e `ValidationErrors` object
 *
 * @returns
 * `true`, if the `ValidationErrors` object `e` contains
 * the errors from the {@link requiresAny}.
 */
export const hasRequiresAnyError = hasProperty(
  VALIDATOR_ERROR_PROP_NAME,
  (u): u is RequiresAnyValidationError => Array.isArray(u),
);

const defaultControlResolver: ControlResolver = ({ parent, root }, paths) =>
  parent && root ? paths.map((path) => root.get(path)).filter(isNotNull) : [];

/**
 * Creates a validator function that applies the same `validator` on
 * multiple controls. If any of these controls fulfilles the validator
 * all of them fulfill the validator.
 *
 * @param paths Paths to the other controls
 * @param resolve Function that resolves the controls
 * @param validator Validator to be executed on each control
 *
 * @returns
 * Validation function
 */
export function requiresAny(
  paths: ControlPath[],
  resolve: ControlResolver = defaultControlResolver,
  validator: ValidatorFn = Validators.required,
): ValidatorFn {
  return (
    control,
  ): Record<
    typeof VALIDATOR_ERROR_PROP_NAME,
    RequiresAnyValidationError
  > | null => {
    const otherControls = resolve(control, paths)
      // Filter potential references to the same control
      .filter((other) => other !== control);

    // Check if either the current field or any of the related fields
    // fulfill the given validator
    const isValid =
      validator(control) === null ||
      otherControls.some((other) => validator(other) === null);

    // If the current field is pristine, we are either in the initial
    // validation phase, where all fields are checked or a related field has
    // been updated, so no need to check the other fields
    if (!control.pristine) {
      for (const other of otherControls) {
        const otherWasValid = !hasRequiresAnyError(other.errors ?? {});
        if (isValid !== otherWasValid) {
          // Postpone the validation until the next invocation of the run
          // loop, because the current validity is not updated yet.
          setTimeout(() => other.updateValueAndValidity());
        }
      }
    }

    return isValid ? null : { [VALIDATOR_ERROR_PROP_NAME]: paths };
  };
}
